import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import { createGlassTheme } from '../theme/glassTheme';

const ThemeModeContext = createContext({ mode: 'light', toggleTheme: () => {} });
const THEME_STORAGE_KEY = 'insights:theme-mode';

const readStoredMode = () => {
    if (typeof window === 'undefined') {
        return null;
    }
    const storedMode = window.localStorage.getItem(THEME_STORAGE_KEY);
    return storedMode === 'dark' || storedMode === 'light' ? storedMode : null;
};

export const ThemeProviderWithMode = ({ children }) => {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const [mode, setMode] = useState(() => readStoredMode() || (prefersDarkMode ? 'dark' : 'light'));
    const [hasStoredPreference, setHasStoredPreference] = useState(() => Boolean(readStoredMode()));

    useEffect(() => {
        if (!hasStoredPreference) {
            setMode(prefersDarkMode ? 'dark' : 'light');
        }
    }, [hasStoredPreference, prefersDarkMode]);

    useEffect(() => {
        document.body.dataset.themeMode = mode;
    }, [mode]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (hasStoredPreference) {
            window.localStorage.setItem(THEME_STORAGE_KEY, mode);
        } else {
            window.localStorage.removeItem(THEME_STORAGE_KEY);
        }
    }, [hasStoredPreference, mode]);

    const toggleTheme = () => {
        setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
        setHasStoredPreference(true);
    };

    const theme = useMemo(() => createGlassTheme(mode), [mode]);

    const contextValue = useMemo(
        () => ({
            mode,
            toggleTheme,
        }),
        [mode]
    );

    return (
        <ThemeModeContext.Provider value={contextValue}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
};

export const useThemeMode = () => useContext(ThemeModeContext);
